import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { fetchPublishedPage } from '@/admin/hooks/useAdminPages';
import { useLanguage } from '@/hooks/useTranslation';
import { Reveal } from '@/components/landing/motion-primitives';
import {
  DocumentFooter,
  DocumentTopBar,
  HeroBackdrop,
  PROSE_CLASS,
} from '@/components/landing/page-kit';
import type { CmsPage, ContentBlock } from '@/types/cms';

// ══════════════════════════════════════════════
// Public CMS Page Renderer
// Renders published CMS pages with the user's language
// ══════════════════════════════════════════════

export default function CmsPageView({ slug: propSlug }: { slug?: string } = {}) {
  const { '*': paramSlug } = useParams();
  const slug = propSlug || paramSlug;
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [page, setPage] = useState<CmsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPublishedPage(slug).then((p) => {
      if (p) {
        setPage(p);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
        <HeroBackdrop />
        <div className="text-center">
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            404
          </p>
          <h1 className="mt-4 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
            {language === 'fr' ? 'Page introuvable' : 'Page not found'}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="group mt-8 inline-flex h-11 items-center justify-center gap-2 rounded-md border border-border px-7 text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
            {language === 'fr' ? 'Retour' : 'Go back'}
          </button>
        </div>
      </div>
    );
  }

  // Select content based on user's language
  const title = language === 'fr' ? page.title_fr || page.title_en : page.title_en || page.title_fr;
  const subtitle =
    language === 'fr' ? page.subtitle_fr || page.subtitle_en : page.subtitle_en || page.subtitle_fr;
  const blocks =
    language === 'fr'
      ? page.content_fr?.length
        ? page.content_fr
        : page.content_en
      : page.content_en?.length
        ? page.content_en
        : page.content_fr;
  const meta =
    language === 'fr'
      ? page.meta_description_fr || page.meta_description_en
      : page.meta_description_en || page.meta_description_fr;
  const heroMeta =
    language === 'fr'
      ? page.hero_meta_fr || page.hero_meta_en
      : page.hero_meta_en || page.hero_meta_fr;

  // Set document title & meta
  if (title) document.title = `${title} — clevia`;
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc && meta) metaDesc.setAttribute('content', meta);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {page.show_header && (
        <DocumentTopBar
          onBack={() => navigate(-1)}
          backLabel={language === 'fr' ? 'Retour' : 'Back'}
          onHome={() => navigate('/')}
        />
      )}

      {/* En-tête du document */}
      {page.show_hero && (
        <div className="relative overflow-hidden border-b border-border px-6 py-20 text-center lg:py-24">
          <HeroBackdrop />
          {/* Teinte propre à la page, choisie dans l'éditeur CMS */}
          <div
            className="pointer-events-none absolute inset-0 -z-10"
            aria-hidden="true"
            style={{
              background: `linear-gradient(to bottom, ${page.hero_gradient_from}14, ${
                page.hero_gradient_to === 'transparent'
                  ? 'transparent'
                  : `${page.hero_gradient_to}14`
              })`,
            }}
          />
          <h1 className="mx-auto max-w-3xl text-balance text-3xl font-semibold leading-[1.12] tracking-tight sm:text-4xl lg:text-[2.75rem]">
            {title}
          </h1>
          {subtitle && (
            <p className="mx-auto mt-5 max-w-xl text-pretty leading-relaxed text-muted-foreground">
              {subtitle}
            </p>
          )}
          {heroMeta && (
            <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              {heroMeta}
            </p>
          )}
        </div>
      )}

      {/* Contenu */}
      <div className="mx-auto max-w-3xl px-6 py-16 lg:py-20">
        <Reveal>
          <div className={PROSE_CLASS}>
            {blocks?.map((block) => (
              <BlockRenderer key={block.id} block={block} />
            ))}
          </div>
        </Reveal>
      </div>

      {page.show_footer && <DocumentFooter />}
    </div>
  );
}

// ══════════════════════════════════════════════
// Block Renderer — converts blocks to HTML
// ══════════════════════════════════════════════

function BlockRenderer({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'heading': {
      const Tag = `h${block.level}` as keyof JSX.IntrinsicElements;
      return <Tag dangerouslySetInnerHTML={{ __html: formatInlineText(block.text) }} />;
    }

    case 'paragraph':
      return <p dangerouslySetInnerHTML={{ __html: formatInlineText(block.text) }} />;

    case 'list': {
      const Tag = block.style === 'numbered' ? 'ol' : 'ul';
      return (
        <Tag>
          {block.items.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: formatInlineText(item) }} />
          ))}
        </Tag>
      );
    }

    case 'callout': {
      // Un seul cadre neutre ; la couleur ne porte que l'icône, comme sur la landing.
      const icons: Record<string, string> = {
        info: 'ℹ️',
        warning: '⚠️',
        success: '✅',
        error: '❌',
      };
      const accents: Record<string, string> = {
        info: 'border-l-blue-500/60',
        warning: 'border-l-amber-500/60',
        success: 'border-l-primary/60',
        error: 'border-l-destructive/60',
      };
      return (
        <div
          className={`not-prose my-8 flex items-start gap-3 rounded-lg border border-l-2 border-border bg-muted/30 p-5 ${
            accents[block.variant] || accents.info
          }`}
        >
          <span className="shrink-0 text-base" aria-hidden="true">
            {icons[block.variant] || icons.info}
          </span>
          <p
            className="m-0 text-sm leading-relaxed text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: formatInlineText(block.text) }}
          />
        </div>
      );
    }

    case 'divider':
      return <hr className="my-10 border-border" />;

    case 'quote':
      return (
        <blockquote className="not-prose my-10 border-l-2 border-primary/40 pl-6">
          <p
            className="text-balance text-lg leading-relaxed text-foreground"
            dangerouslySetInnerHTML={{ __html: formatInlineText(block.text) }}
          />
          {block.author && (
            <footer className="mt-3 text-sm text-muted-foreground">— {block.author}</footer>
          )}
        </blockquote>
      );

    case 'spacer': {
      const heights: Record<string, string> = { sm: 'h-4', md: 'h-8', lg: 'h-16' };
      return <div className={heights[block.size] || 'h-8'} />;
    }

    case 'image':
      return (
        <figure className="not-prose my-10">
          <img
            src={block.url}
            alt={block.alt}
            className="w-full rounded-xl border border-border"
            loading="lazy"
          />
          {block.caption && (
            <figcaption className="mt-3 text-center text-sm text-muted-foreground">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );

    case 'table':
      return (
        <div className="not-prose my-8 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {block.headers.map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left font-medium text-foreground"
                    dangerouslySetInnerHTML={{ __html: formatInlineText(h) }}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-border last:border-0">
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-4 py-3 text-muted-foreground"
                      dangerouslySetInnerHTML={{ __html: formatInlineText(cell) }}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
}

// ── Inline text formatting (simple markdown-like) ──

function formatInlineText(text: string): string {
  if (!text) return '';
  // Preserve existing safe HTML tags by replacing them with placeholders
  const PLACEHOLDER_STRONG_OPEN = '%%STRONG_OPEN%%';
  const PLACEHOLDER_STRONG_CLOSE = '%%STRONG_CLOSE%%';
  const PLACEHOLDER_EM_OPEN = '%%EM_OPEN%%';
  const PLACEHOLDER_EM_CLOSE = '%%EM_CLOSE%%';
  const safeTags: [RegExp, string, string][] = [
    [/<strong>(.*?)<\/strong>/g, PLACEHOLDER_STRONG_OPEN, PLACEHOLDER_STRONG_CLOSE],
    [/<em>(.*?)<\/em>/g, PLACEHOLDER_EM_OPEN, PLACEHOLDER_EM_CLOSE],
  ];
  let result = text;
  for (const [regex, open, close] of safeTags) {
    result = result.replace(regex, (_m, content) => `${open}${content}${close}`);
  }
  // Escape remaining HTML
  result = escapeHtml(result);
  // Restore safe tags
  result = result
    .replace(new RegExp(PLACEHOLDER_STRONG_OPEN, 'g'), '<strong>')
    .replace(new RegExp(PLACEHOLDER_STRONG_CLOSE, 'g'), '</strong>');
  result = result
    .replace(new RegExp(PLACEHOLDER_EM_OPEN, 'g'), '<em>')
    .replace(new RegExp(PLACEHOLDER_EM_CLOSE, 'g'), '</em>');
  // Markdown bold: **text**
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Markdown italic: *text*
  result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Links: [text](url)
  result = result.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" class="text-brand-700 dark:text-brand-400 hover:underline">$1</a>'
  );
  return result;
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (c) => map[c]);
}
